import { useCallback, useEffect, useMemo } from 'react';
import { atomFamily, useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import type { AtomEffect, RecoilState, SetterOrUpdater } from 'recoil';
import equals from 'ramda/es/equals';
import { broadcastChannelSynchronizerEffect } from 'utils/recoilEffects';
import {
  EXCLUDED_AUTOLOADED_PRIOR_STUDIES_PARAM,
  PRIOR_STUDIES_PARAM,
  getCaseStudyParamsSuffix,
} from './studyParams';

export type ActivePriorStudiesState = {
  priors: ReadonlyArray<string>;
  excluded: ReadonlyArray<string>;
};

const EMPTY: ActivePriorStudiesState = { priors: [], excluded: [] };

const parseCsv = (raw: Nullable<string>): string[] => {
  if (raw == null || raw.length === 0) return [];
  // Dedupe defensively (some legacy URLs repeat smids). A Set preserves
  // first-occurrence order, so user intent is kept.
  const seen = new Set<string>();
  for (const part of raw.split(',')) {
    if (part.length > 0) seen.add(part);
  }
  return [...seen];
};

/**
 * Atom effect that seeds the atom from the current URL search params on first
 * subscription. Runs synchronously during `useRecoilValue`, so consumers see
 * the URL-derived value on their very first render — important to avoid
 * firing the GBVD query with empty `studyIds` on a deep-linked load.
 *
 * The effect only seeds when there is something in the URL; otherwise the
 * atom stays at its default and no `setSelf` happens.
 */
const urlHydrationEffect: AtomEffect<ActivePriorStudiesState> = ({ setSelf, trigger }) => {
  if (trigger !== 'get') return;
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  const priors = parseCsv(params.get(PRIOR_STUDIES_PARAM));
  const excluded = parseCsv(params.get(EXCLUDED_AUTOLOADED_PRIOR_STUDIES_PARAM));
  if (priors.length > 0 || excluded.length > 0) {
    setSelf({ priors, excluded });
  }
};

/**
 * Holds the active comparative-prior smids for a case.
 *
 * Source-of-truth for the viewer; the URL is a *projection* of this atom (see
 * `ActivePriorStudiesUrlSync` below). Synced across windows via BroadcastChannel
 * so multi-monitor viewers stay in step. Not persisted to localStorage — the
 * URL is the persistence layer.
 *
 * Keyed by case smid. The empty string ('') is reserved as a sentinel for
 * "no current case" so callers can pass `caseSmid ?? ''` without crashing.
 */
export const activePriorStudiesState: (caseSmid: string) => RecoilState<ActivePriorStudiesState> =
  atomFamily<ActivePriorStudiesState, string>({
    key: 'activePriorStudies',
    default: EMPTY,
    effects: [urlHydrationEffect, broadcastChannelSynchronizerEffect()],
  });

/**
 * Read the current value of the atom for a given case. Returns the EMPTY
 * sentinel when no case is active.
 */
export const useActivePriorStudiesValue = (caseSmid: Nullable<string>): ActivePriorStudiesState => {
  // Read the '' sentinel atom when there's no case, but never surface its
  // (URL-hydratable) value: return EMPTY so a null caseSmid can't leak the
  // current URL's priors into a caller waiting on unresolved params.
  const casePriorStudies = useRecoilValue(activePriorStudiesState(caseSmid ?? ''));
  return useMemo(() => (caseSmid != null ? casePriorStudies : EMPTY), [caseSmid, casePriorStudies]);
};

/**
 * Read + write the active prior smids for a case. Components should typically
 * read via `useActivePriorStudiesValue` and write via `useCurrentComparativeStudies`
 * (which handles cross-case navigation). Use this directly only when you
 * already know you are operating on the current case.
 */
export const useActivePriorStudies = (
  caseSmid: Nullable<string>
): [ActivePriorStudiesState, SetterOrUpdater<ActivePriorStudiesState>] =>
  useRecoilState(activePriorStudiesState(caseSmid ?? ''));

/**
 * Read the prior smids for a case as a `string[]` (memoized for stability).
 * Convenience wrapper for callers that don't need the excluded list.
 */
export const useActivePriorSmids = (caseSmid: Nullable<string>): ReadonlyArray<string> => {
  const { priors } = useActivePriorStudiesValue(caseSmid);
  return priors;
};

const computeUrlSuffix = (state: ActivePriorStudiesState): string => {
  // useUrlBuilder strips autoloaded smids that the user kept in `priors` and
  // adds excluded autoloaded smids. The atom holds the post-filter view, so we
  // serialize the lists directly.
  const priors = state.priors.join(',');
  const excluded = state.excluded.join(',');
  return getCaseStudyParamsSuffix(priors, excluded);
};

/**
 * Replace the prior_studies and skip_studies search params on the current
 * window.location with the given suffix without notifying react-router. The
 * URL is updated in-place (so refresh/share works) but no `useSearchParams`
 * subscriber re-renders.
 */
const replaceUrlParams = (suffix: string): void => {
  if (typeof window === 'undefined') return;
  const { pathname, search, hash } = window.location;

  // Preserve unrelated params (e.g. `hp=create`) but drop our own so we can
  // re-append them from the suffix.
  const preserved = new URLSearchParams(search);
  preserved.delete(PRIOR_STUDIES_PARAM);
  preserved.delete(EXCLUDED_AUTOLOADED_PRIOR_STUDIES_PARAM);

  // Append the suffix with RAW commas (matching `buildUrl`). We deliberately do
  // NOT route it through `URLSearchParams.set`, which would percent-encode the
  // comma smid-separators to `%2C` and uglify the shareable URL.
  const ours = suffix.startsWith('?') ? suffix.slice(1) : suffix;
  const combined = [preserved.toString(), ours].filter((s) => s.length > 0).join('&');
  const nextSearch = combined.length > 0 ? `?${combined}` : '';

  const next = `${pathname}${nextSearch}${hash}`;
  if (next !== `${pathname}${search}${hash}`) {
    window.history.replaceState(window.history.state, '', next);
  }
};

const scheduleIdle = (cb: () => void): (() => void) => {
  if (typeof window === 'undefined') {
    cb();
    return () => {};
  }
  const ric = (window as unknown as { requestIdleCallback?: (cb: () => void) => number })
    .requestIdleCallback;
  const cic = (window as unknown as { cancelIdleCallback?: (id: number) => void })
    .cancelIdleCallback;
  if (typeof ric === 'function') {
    const id = ric(cb);
    return () => cic?.(id);
  }
  const id = window.setTimeout(cb, 0);
  return () => window.clearTimeout(id);
};

// Cases with an atom→URL write scheduled but not yet flushed. While a case is
// here the atom is ahead of the URL, so hydrating from the (stale) URL would
// clobber the user's just-made prior selection. Keyed by caseSmid.
const pendingUrlWrites = new Set<string>();

type UrlSyncProps = {
  caseSmid: Nullable<string>;
  /**
   * Read URL search params. Defaults to `window.location.search`. Tests can
   * inject a stub.
   */
  readSearch?: () => string;
};

/**
 * Bidirectional sync between the active-priors atom and the URL.
 *
 * - On mount and on case change, hydrates the atom from URL params if the
 *   URL list differs from the atom (URL wins on hydration, reflecting
 *   deep-link / refresh / paste-into-new-tab semantics).
 * - On atom change, mirrors the value back to the URL via
 *   `history.replaceState` inside an idle callback. We deliberately do NOT
 *   use `setSearchParams` from react-router — that would notify every
 *   `useSearchParams` consumer and re-trigger the render storm we're fixing.
 * - On `popstate` (back/forward), re-hydrates the atom from URL.
 *
 * Mount this once near the viewer/reporter root. Multiple mounts for the
 * same caseSmid are safe but redundant.
 */
export const ActivePriorStudiesUrlSync = ({ caseSmid, readSearch }: UrlSyncProps): null => {
  useActivePriorStudiesUrlSync({ caseSmid, readSearch });
  return null;
};

/**
 * Hook form of `ActivePriorStudiesUrlSync` for callers that want to compose
 * with other root-level effects.
 */
export const useActivePriorStudiesUrlSync = ({ caseSmid, readSearch }: UrlSyncProps): void => {
  const setState = useSetRecoilState(activePriorStudiesState(caseSmid ?? ''));
  const value = useActivePriorStudiesValue(caseSmid);

  const getSearch = useCallback(
    () => (readSearch != null ? readSearch() : window.location.search),
    [readSearch]
  );

  // Hydrate from URL on mount/caseSmid change and on popstate.
  useEffect(() => {
    if (caseSmid == null) return;
    const readFromUrl = (): ActivePriorStudiesState => {
      const params = new URLSearchParams(getSearch());
      return {
        priors: parseCsv(params.get(PRIOR_STUDIES_PARAM)),
        excluded: parseCsv(params.get(EXCLUDED_AUTOLOADED_PRIOR_STUDIES_PARAM)),
      };
    };
    // Initial hydrate: skip while our own atom→URL write is in flight. The atom
    // is the source of truth and is ahead of the URL, so hydrating (e.g. from a
    // sibling mounting in that window) would revert the user's selection.
    if (!pendingUrlWrites.has(caseSmid)) {
      const fromUrl = readFromUrl();
      setState((prev) => (equals(prev, fromUrl) ? prev : fromUrl));
    }
    // popstate is an explicit user navigation — it supersedes any pending mirror
    // write, so drop it and hydrate from the new URL unconditionally.
    const onPop = () => {
      pendingUrlWrites.delete(caseSmid);
      const fromUrl = readFromUrl();
      setState((prev) => (equals(prev, fromUrl) ? prev : fromUrl));
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
    // We intentionally re-subscribe whenever the case changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseSmid, setState]);

  // Mirror atom → URL via replaceState inside an idle callback. Skipped when
  // there is no current case (atom is keyed by '' in that case).
  const suffix = useMemo(() => computeUrlSuffix(value), [value]);
  useEffect(() => {
    if (caseSmid == null) return;
    pendingUrlWrites.add(caseSmid);
    const cancel = scheduleIdle(() => {
      replaceUrlParams(suffix);
      pendingUrlWrites.delete(caseSmid);
    });
    return () => {
      cancel();
      pendingUrlWrites.delete(caseSmid);
    };
  }, [caseSmid, suffix]);
};
